import { useState } from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Button
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { SoftwareKitSelect, SoftwareKitConfigSelect } from '.';
import LoadKitsTable from './LoadKitsTable';
import KitTargetSystemSelect from './KitTargetSystemSelect';

// determines whether the Kit Type specified will be disabled due to a limitation we currently have where
// we cannot load multiple kits of different types at the Rack Details Loading section. This is supported, however, during rack provisioning
function isKitTypeDisabled(kitType, kitList, provision) {
  // checks if there's any kits in the existing list that are not the type specified
  if (provision) {
    return false;
  }

  switch (kitType) {
    case 'software':
      return kitList.media.length > 0 || kitList.content.length > 0;
    case 'content':
      return kitList.media.length > 0 || kitList.software.length > 0;
    case 'media':
      return kitList.software.length > 0 || kitList.content.length > 0;
    default:
      return false;
  }
}

LoadKits.propTypes = {
  kitList: PropTypes.object,
  setKitList: PropTypes.func,
  systemType: PropTypes.string,
  provision: PropTypes.bool
};

export default function LoadKits({ kitList, setKitList, systemType, provision }) {
  const [kitID, setKitID] = useState('');
  const [kitIDType, setKitIDType] = useState('kit_release_id');
  const [kitType, setKitType] = useState('software');
  const [kitSystemType, setKitSystemType] = useState('');
  const [kitConfig, setKitConfig] = useState(null);
  const [kitTargetSystem, setKitTargetSystem] = useState('');
  const [addKitButtonDisabled, setAddKitButtonDisabled] = useState(true);

  const isAddKitDisabled = addKitButtonDisabled || kitID === '';

  const setKitTypeChange = (event) => {
    setKitType(event.target.value);
    // clear kit info when type is changed
    clearKit();
  };

  // clears the information set on the current kit
  const clearKit = () => {
    setKitID('');
    setKitIDType('kit_release_id');
    setKitConfig(null);
    setKitTargetSystem(null);
    setKitSystemType(null);
  };

  // the handler that adds a kit to the kit list
  const handleAddKit = () => {
    const newKitList = { ...kitList };
    const newKit = {};
    newKit[kitIDType] = parseInt(kitID, 10);
    // kit had a target system type if decoupled
    if (kitTargetSystem && systemType === 'decoupled') {
      newKit.system = kitTargetSystem;
    } else if (systemType !== 'decoupled') {
      newKit.system = kitSystemType;
      newKit.system = newKit.system.toLowerCase() === 'gcs' ? 'gcs' : 'ife';
    }
    // software kit had a kit config specified
    if (kitConfig && kitType === 'software') {
      newKit.kit_config = kitConfig;
    }
    // add the kit to the list
    newKitList[kitType].push(newKit);
    setKitList(newKitList);
    clearKit();
    setAddKitButtonDisabled(true);
  };

  return (
    <Grid data-testid="load-kits-section" container spacing={3} paddingTop={1}>
      <Grid item xs={12}>
        <FormControl sx={{ paddingLeft: 1 }}>
          <FormLabel>Kit Type</FormLabel>
          <RadioGroup row value={kitType} onChange={setKitTypeChange}>
            <FormControlLabel
              value="software"
              control={<Radio size="small" />}
              label="Software"
              disabled={isKitTypeDisabled('software', kitList, provision)}
            />
            <FormControlLabel
              value="content"
              control={<Radio size="small" />}
              label="Content"
              disabled={isKitTypeDisabled('content', kitList, provision)}
            />
            <FormControlLabel
              value="media"
              control={<Radio size="small" />}
              label="Media"
              disabled={isKitTypeDisabled('media', kitList, provision)}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <SoftwareKitSelect
          kitID={kitID}
          setKitID={setKitID}
          kitIDType={kitIDType}
          setKitIDType={setKitIDType}
          setKitSystemType={setKitSystemType}
          kitSystemType={kitSystemType}
          addKitButtonDisabled={addKitButtonDisabled}
          setAddKitButtonDisabled={setAddKitButtonDisabled}
          kitList={kitList}
        />
      </Grid>
      {kitType === 'software' && (
        <Grid item xs={12} sm={6}>
          <SoftwareKitConfigSelect
            kitID={kitID}
            kitIDType={kitIDType}
            kitConfig={kitConfig}
            setKitConfig={setKitConfig}
          />
        </Grid>
      )}
      {systemType === 'decoupled' && (
        <Grid item xs={12}>
          <KitTargetSystemSelect
            kitTargetSystem={kitTargetSystem}
            setKitTargetSystem={setKitTargetSystem}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddKit}
          disabled={isAddKitDisabled}
        >
          Add Kit
        </Button>
      </Grid>
      <Grid item xs={12}>
        <LoadKitsTable kitList={kitList} setKitList={setKitList} />
      </Grid>
    </Grid>
  );
}
